'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ReferenceChrome, Container, SectionLabel, SectionRule } from '@/components/pages/reference-landing/ReferenceChrome';
import { NavbarRef } from '@/components/pages/reference-landing/sections/NavbarRef';
import { FooterRef } from '@/components/pages/reference-landing/sections/FooterRef';

function CheckIcon() {
  return (
    <svg className="mt-0.5 size-4 shrink-0 text-[#129990]" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function HowItWorksContent() {
  const router = useRouter();

  return (
    <ReferenceChrome>
      <NavbarRef />

      {/* ── Hero ── */}
      <section className="relative w-full pt-14 pb-0 sm:pt-16">
        
        <Container className="relative z-10 text-center">
          <h1 className="mx-auto mt-14 max-w-3xl text-[2.5rem] font-normal leading-[1.15] tracking-tight text-gray-900 sm:text-5xl md:text-[3.5rem]">
            <span className="block">Scan. Write. Rank.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base font-light leading-relaxed text-gray-500 sm:text-lg">
            60-second setup delivers instant value. Automated daily scans identify competitor keyword gaps. RankGrip generates complete SEO content and publishes them to your CMS on autopilot.
          </p>
        </Container>
      </section>

      <main>
        {/* ── Steps Section ── */}
        <section className="py-16 sm:py-20">
          <Container className="text-center">
            <SectionLabel>Workflow</SectionLabel>
            <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[2rem]">
              The Autopilot Workflow
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-gray-500">
              RankGrip transforms keyword research and content writing from manual chores into automated growth. Our Scan → Write → Publish loop puts your SEO on autopilot.
            </p>
          </Container>

          {/* Step 1 */}
          <Container className="mt-14">
            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-16">
              <div>
                <SectionLabel>Step 1</SectionLabel>
                <h3 className="mt-3 text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[1.75rem]">
                  Scan Competitor Keyword Gaps
                </h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-gray-500 sm:text-base">
                  60-second setup: add your domain and competitors. Our AI runs an instant keyword gap scan analyzing what queries your competitors are ranking for on Google. We identify exactly which terms are winnable.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Track winnable keyword opportunities on Google',
                    'Scan 5-15 competitor websites automatically',
                    'Real-time search volume and keyword difficulty analysis',
                    'Google Mobile and News indexing coverage scan',
                    'Winnability score showing which gaps to fix first',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <CheckIcon />
                      <span className="text-sm font-light text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-full rounded-sm border border-gray-200 bg-gray-50/60 p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <svg className="size-5 text-[#129990]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                    <h4 className="text-sm font-semibold text-gray-900">Winnability Scan</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="h-2 rounded-full bg-gray-100">
                        <div className="h-2 rounded-full bg-[#129990]" style={{ width: '72%' }} />
                      </div>
                      <div className="mt-1 flex justify-between text-xs text-gray-500">
                        <span>Overall Winnability</span>
                        <span>72/100</span>
                      </div>
                    </div>
                    <div className="rounded-sm border border-gray-100 bg-gray-50 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckIcon />
                        <span className="text-xs font-medium text-gray-700">Gap Found</span>
                      </div>
                      <p className="text-xs text-gray-500">&quot;best project management for startups&quot; — Competitor ranks #1</p>
                    </div>
                    <div className="rounded-sm border border-gray-100 bg-gray-50 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="size-3.5 text-[#129990]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                        </svg>
                        <span className="text-xs font-medium text-gray-700">Competitor Lead</span>
                      </div>
                      <p className="text-xs text-gray-500">Competitor mentioned 8/10 times. You: 2/10</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>

          {/* Step 2 */}
          <Container className="mt-16 sm:mt-20">
            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-16">
              <div className="md:order-2">
                <SectionLabel>Step 2</SectionLabel>
                <h3 className="mt-3 text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[1.75rem]">
                  Generate &amp; Auto-Publish SEO Content
                </h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-gray-500 sm:text-base">
                  No more writing manual drafts. Click &quot;Publish Autopilot&quot; to let our writing engine generate fully-researched, formatted SEO content targeting the keyword gap. Complete with media, headers, and internal links.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'One-Click generation of complete SEO content',
                    'Auto-publishing to WordPress, Webflow, and Shopify',
                    'Schema markup suggestions for rich results',
                    'Internal linking automation to pass page authority',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <CheckIcon />
                      <span className="text-sm font-light text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-full rounded-sm border border-gray-200 bg-gray-50/60 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <svg className="size-5 text-[#129990]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                      </svg>
                      <h4 className="text-sm font-semibold text-gray-900">Action Center</h4>
                    </div>
                    <span className="rounded-sm bg-[#129990] px-2 py-0.5 text-[10px] font-semibold text-white">3 High Impact</span>
                  </div>
                  <div className="space-y-3">
                    {[
                      { title: 'Best PM Tools Competitor Gap', impact: 'Winnability: 92%', traffic: '~2.4K/mo', color: 'green' },
                      { title: 'Schema: Product Reviews', impact: 'Impact: High', traffic: 'Effort: 10 min', color: 'blue' },
                      { title: 'Content Refresh: 2023 Guide', impact: 'Decay: -40%', traffic: 'Quick win', color: 'white' },
                    ].map((item) => (
                      <div key={item.title} className={`rounded-sm border p-3 ${item.color === 'green' ? 'border-green-100 bg-green-50' : item.color === 'blue' ? 'border-blue-100 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{item.title}</span>
                          <button
                            type="button"
                            onClick={() => router.push('/sign-up')}
                            className="rounded-sm bg-[#129990] px-2 py-0.5 text-[10px] font-medium text-white hover:bg-[#0D736F]"
                          >
                            Fix This
                          </button>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{item.impact}</span>
                          <span>{item.traffic}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Container>

          {/* Step 3 */}
          <Container className="mt-16 sm:mt-20">
            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-16">
              <div>
                <SectionLabel>Step 3</SectionLabel>
                <h3 className="mt-3 text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[1.75rem]">
                  Auto-Index &amp; Track Google Ranks
                </h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-gray-500 sm:text-base">
                  Make sure Google sees your new content instantly. RankGrip auto-submits your new posts to search engine indexes and tracks your ranking progress in real-time. Watch your keywords climb to the first page.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Automated Google Search Console and Indexing API sync',
                    'Daily ranking dashboard tracking position changes',
                    'Compounding traffic projection maps',
                    'Unlimited team seats on ALL plans (no collaboration tax)',
                    'White-label reports for agencies & consultants (Pro)',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <CheckIcon />
                      <span className="text-sm font-light text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-full rounded-sm border border-gray-200 bg-gray-50/60 p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <svg className="size-5 text-[#129990]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                    </svg>
                    <h4 className="text-sm font-semibold text-gray-900">Rank Tracking</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700">Organic Impressions (30d)</span>
                        <span className="text-sm font-medium text-green-600">+82%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100">
                        <div className="h-2 rounded-full bg-green-500" style={{ width: '82%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700">Average Position</span>
                        <span className="text-sm font-medium text-[#129990]">#4.2</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100">
                        <div className="h-2 rounded-full bg-[#129990]" style={{ width: '100%' }} />
                      </div>
                    </div>
                    <div className="rounded-sm border border-gray-100 bg-gray-50 p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Organic Clicks</p>
                          <p className="text-lg font-medium text-gray-900">4,180</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Keywords on Page 1</p>
                          <p className="text-lg font-medium text-gray-900">84</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ── Speed Section ── */}
        <section className="py-16 sm:py-20 bg-gray-50/60">
          <Container className="text-center">
            <SectionLabel>Efficiency</SectionLabel>
            <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-normal leading-snug tracking-tight text-gray-900 sm:text-[2rem]">
              Speed, Simplicity, Automation
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-gray-500">
              RankGrip eliminates manual keyword research and writing. Automated daily scans, AI-prioritized keywords, one-click publishing, and automated rank tracking.
            </p>
          </Container>

          <Container className="mt-10">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {[
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                  ),
                  title: 'Value in Under 2 Minutes',
                  desc: '60-second setup delivers instant keyword gap analysis. Land in the dashboard with prioritized targets. Click "Publish Autopilot" to launch your first post.',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  ),
                  title: 'Automated Daily Scans',
                  desc: 'AI scans competitor keywords daily. Identifies new gaps, competitor ranking shifts, and indexing status automatically.',
                },
                {
                  icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                    </svg>
                  ),
                  title: 'No Collaboration Tax',
                  desc: 'Unlimited team seats on ALL plans means your entire team can collaborate from day one without scaling your bill. Share dashboards, assign tasks, track progress together.',
                },
              ].map((card) => (
                <div key={card.title} className="rounded-sm border border-gray-200 bg-white p-6 sm:p-8">
                  <span className="flex size-9 items-center justify-center rounded-sm border border-gray-200 bg-gray-50 text-[#129990]">
                    {card.icon}
                  </span>
                  <h3 className="mt-4 text-xl font-normal text-gray-900">{card.title}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-gray-500">{card.desc}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── CTA ── */}
        <section className="py-16 sm:py-20 bg-[#129990]">
          <Container className="text-center">
            <h2 className="text-2xl font-normal leading-tight tracking-tight text-white sm:text-[2.25rem]">
              Ready to Put Your SEO on Autopilot?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-white/80">
              Join founders and marketers using RankGrip to scan competitor keyword gaps, generate SEO content, and publish to their CMS on autopilot. 7-day free trial, cancel anytime.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="relative flex items-center justify-center rounded-md border border-white/20 bg-white px-8 py-3 text-sm font-medium text-[#129990] shadow-[inset_0_1px_0px_rgba(255,255,255,0.4),0_0_0_1px_rgba(255,255,255,0.1),0_2px_6px_0_rgba(255,255,255,0.15)] transition-all duration-75 hover:brightness-105 active:translate-y-[2px] active:shadow-[inset_0_1px_0px_rgba(255,255,255,0.2),0_0_0_1px_rgba(255,255,255,0.1)]"
              >
                Start Free Trial
              </Link>
              <Link
                href="/why-rankgrip"
                className="relative flex items-center justify-center rounded-md border border-white/40 bg-transparent px-8 py-3 text-sm font-medium text-white shadow-[inset_0_1px_0px_rgba(255,255,255,0.1),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-75 hover:bg-white/10 active:translate-y-[2px] active:shadow-none"
              >
                Why Choose RankGrip
              </Link>
            </div>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-6">
              {['7-day free trial', 'No credit card required', 'Cancel anytime'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm font-light text-white/80">
                  <svg className="size-4 shrink-0 text-white" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </Container>
        </section>
      </main>

      <FooterRef />
    </ReferenceChrome>
  );
}
